import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import type { LaunchItem } from './launchSummaryDetail';

export interface LaunchDetailGroup {
  /** Shown above the group's list when a disclosure carries more than one kind of item. */
  heading?: string;
  items: readonly LaunchItem[];
}

export interface LaunchDetailProps {
  /** Unique on the page — the body's id, for `aria-controls`. */
  id: string;
  /**
   * What the list reveals, with its count — e.g. `7 assets not yet placed`. The toggle's accessible
   * name is this after the verb it shows (*Show the …* / *Hide the …*), so the two names cannot
   * describe different things and the visible word is always inside the name.
   */
  label: string;
  groups: readonly LaunchDetailGroup[];
  className?: string;
}

/**
 * The closed-by-default list under a Launch count (`launch-summary-detail`, R1).
 *
 * ⚠ **Hand-rolled, and that is a RULING rather than a shortcut.** D18 of the design-system epic
 * (Rob, 2026-09-05) allows a token-laid-out disclosure until the Accordion card lands, and Rob chose
 * that interim for this page on 2026-09-28. `@repo/design-system` has no disclosure primitive,
 * `Panel` is Fluent-backed and `TLAccordion` is a §6A legacy item. §6B's accordion entry names this
 * surface as a consumer, so the card's own inventory finds it. Nothing here invents a chevron, a
 * border or motion: a text `Button` and a plain list, every value a token — the same shape as the
 * Equipment step's import controls (`AssetCsvUploader`), without their automatic collapse, which
 * nothing on Launch needs.
 *
 * - **Closed on every mount.** State is local and unpersisted, so leaving Launch and coming back
 *   closes every list again — Rob's "by default closed".
 * - **Absent when empty.** A *Show* that reveals nothing is noise, so nothing renders at all.
 * - **Conditionally rendered body**, so `aria-controls` is omitted while it is closed rather than
 *   pointing at an id nothing renders; `aria-expanded` carries the state on its own.
 * - **Keyed by id**, never by name: display names are not unique on a plan or a register.
 * - **Every item, uncapped.** Closed by default is what keeps a large gym's page short; a slice
 *   would present part of the list as the whole.
 */
const LaunchDetail: React.FC<LaunchDetailProps> = ({ id, label, groups, className }) => {
  const [open, setOpen] = useState(false);
  const shown = groups.filter(group => group.items.length > 0);
  if (shown.length === 0) return null;

  return (
    <div className={className}>
      <Button
        variant="text"
        size="xs"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={`${open ? 'Hide' : 'Show'} the ${label}`}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Hide' : 'Show'}
      </Button>
      {open && (
        <div id={id} className="launch-detail-body">
          {shown.map((group, g) => (
            <div key={group.heading ?? g} className="launch-detail-group">
              {/* A div, not a <p>: the Launch page styles every <p> in a block as body copy, and
                  that selector outranks this one — caught in the PUL-CON capture. It labels its
                  list, so a screen reader moving by list hears which group it is in. */}
              {group.heading ? (
                <div id={`${id}-group-${g}`} className="launch-detail-heading">
                  {group.heading}
                </div>
              ) : null}
              <ul aria-labelledby={group.heading ? `${id}-group-${g}` : undefined}>
                {group.items.map(item => (
                  <li key={item.id}>
                    {item.name}
                    {item.detail ? (
                      <span className="launch-detail-extra"> — {item.detail}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default styled(LaunchDetail)`
  margin-top: ${space[1]};

  .launch-detail-body {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    margin-top: ${space[1]};
  }

  .launch-detail-heading {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${ink[4]};
  }

  ul {
    margin: 0;
    padding-left: ${space[5]};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${ink[2]};
  }

  .launch-detail-extra {
    color: ${ink[4]};
  }
`;
